/* eslint-disable no-restricted-syntax -- amber token for failed status, theme has no warning colour */
import { useTranslation } from 'react-i18next';
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useIsPublishingEnabled } from '@utils/api/connections/use-is-publishing-enabled.tsx';
import { useGetDocumentPublishHistory } from '@utils/api/publishing/use-get-document-publish-history.tsx';
import { getPlatformSpec } from '@utils/types/connections/platforms.ts';
import { formatDateYearMonthDay } from '@utils/date.ts';

const WARNING_ICON = '#B45309';

interface Props {
  sessionId: string;
  documentId: string;
  /** Opens the full publish-history modal when the inline summary is clicked. */
  onOpenHistory: () => void;
}

/**
 * Compact "Last published" chip for the editor-header subtitle row.
 * Shows the most recent attempt's platform + status + date, clickable
 * to open the full history. Renders nothing when the org has no
 * publishing entitlement or the document has no publish history yet.
 */
export default function PublishLatestInline({
  sessionId,
  documentId,
  onOpenHistory
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { isEnabled: isPublishingEnabled } = useIsPublishingEnabled();
  const { data } = useGetDocumentPublishHistory(
    sessionId,
    documentId,
    isPublishingEnabled
  );

  if (!isPublishingEnabled) return null;
  const latest = data?.[0];
  if (!latest) return null;

  const spec = getPlatformSpec(latest.platform);
  const date = latest.published_at
    ? formatDateYearMonthDay(latest.published_at)
    : null;

  const statusGlyph = (() => {
    if (latest.status === 'succeeded') {
      return (
        <CheckCircle2
          size={12}
          aria-hidden="true"
          style={{ color: getColour('success') }}
        />
      );
    }
    if (latest.status === 'failed') {
      return (
        <AlertCircle
          size={12}
          aria-hidden="true"
          style={{ color: WARNING_ICON }}
        />
      );
    }
    if (latest.status === 'cancelled') {
      return (
        <Send
          size={12}
          aria-hidden="true"
          style={{ color: getColour('inactiveText') }}
        />
      );
    }
    return (
      <Loader2
        size={12}
        aria-hidden="true"
        className="animate-spin"
        style={{ color: getColour('inactiveText') }}
      />
    );
  })();

  return (
    <button
      type="button"
      onClick={onOpenHistory}
      aria-label={t('publish-history-title')}
      className="flex items-center gap-1 whitespace-nowrap shrink-0 rounded-md px-1.5 py-0.5 hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      style={{ color: getColour('inactiveText') }}
    >
      <Send size={12} aria-hidden="true" style={{ color: getColour('icon') }} />
      <span className="font-medium" style={{ color: getColour('normalText') }}>
        {spec?.displayName ?? latest.platform}
      </span>
      {statusGlyph}
      {date && <span>{date}</span>}
    </button>
  );
}
